import React, { useEffect, useRef } from 'react';
import useCheckoutState from '../hooks/useCheckoutState';
import clsx from 'clsx';
import useCheckoutTicketFormHelpers from '../hooks/useCheckoutTicketFormHelpers';
import TicketType from './TicketType';
import checkIsTicketTypeVisible from '../lib/checkIsTicketTypeVisible';
import checkIsTicketTypeScheduled from '../lib/checkIsTicketTypeScheduled';

export default function TicketGroup({
  ticketGroup,
  onSelectTicketGroup,
  isAccordion,
  isTicketGroupVisible,
  isTicketGroupScheduled,
  ticketGroupHasInvalidReference,
}) {
  const { page, form, query, isPreview, updateDraftChargeDebounced } = useCheckoutState();
  const { ticketGroups: formTicketGroups } = form;
  const { setTicketType, unsetTicketType } = useCheckoutTicketFormHelpers();

  // Track previous ticket group state to detect changes
  const prevTicketGroupsRef = useRef();

  useEffect(() => {
    // Only trigger updateDraftCharge if ticketGroups actually changed
    if (
      prevTicketGroupsRef.current &&
      JSON.stringify(prevTicketGroupsRef.current) !== JSON.stringify(formTicketGroups)
    ) {
      updateDraftChargeDebounced();
    }
    prevTicketGroupsRef.current = formTicketGroups;
  }, [formTicketGroups, updateDraftChargeDebounced]);

  const allTicketTypes = page.ticketGroups?.flatMap(group => group.ticketTypes) || [];

  const ticketTypes = ticketGroup.ticketTypes.filter(ticketType => ticketType.status === 'enabled');

  if (ticketTypes.length === 0) {
    return null;
  }

  const isEventSoldOut = page.eventDetails?.isSoldOut;
  const isEventCanceled = page.eventDetails.status === 'canceled';
  const isTicketGroupUnavailable = ticketGroup.availabilityStatus === 'unavailable';

  if (!isTicketGroupVisible && !isPreview) {
    return null;
  }

  const showTicketGroupName = !isAccordion && ticketGroup.layout?.showTicketGroupName;

  if (ticketGroup.ticketSelectionType === 'quantity') {
    return (
      <div
        className={clsx('cp_ticket-group', `cp_ticket-group-${ticketGroup.reference}`, {
          hidden: !isTicketGroupVisible && !isPreview,
        })}
      >
        {(showTicketGroupName || ticketGroup.description) && (
          <div className="mb-1">
            {showTicketGroupName && (
              <span className="cp_ticket-group-name font-semibold">{ticketGroup.name}</span>
            )}

            {ticketGroup.description && (
              <div className="cp_ticket-group-description text-gray-700 text-sm">
                {ticketGroup.description}
              </div>
            )}
          </div>
        )}

        <div
          className={clsx('cp_ticket-group-ticket-types', {
            'grid gap-2': ticketGroup.layout?.type === 'grid',
            '-space-y-px': ticketGroup.layout?.type !== 'grid',
          })}
          style={{
            ...(ticketGroup.layout?.type === 'grid' && {
              gridTemplateColumns: `repeat(${ticketGroup.layout?.columns}, minmax(0, 1fr))`,
            }),
          }}
        >
          {ticketTypes.map(ticketType => {
            const formTicketType = formTicketGroups?.[ticketGroup._id]?.find(
              formTicketGroup => formTicketGroup.ticketTypeId === ticketType._id,
            );

            function setQuantity(quantity) {
              setTicketType(ticketGroup._id, ticketType._id, quantity);
            }

            const isSoldOut = isEventSoldOut || ticketGroup.isSoldOut || ticketType.isSoldOut;

            const isTicketTypeUnavailable = ticketType.availabilityStatus === 'unavailable';
            const isUnavailable = isTicketGroupUnavailable || isTicketTypeUnavailable;

            const isOnSale = ticketGroup.isOnSale && ticketType.isOnSale;

            const ticketTypeScheduleResult = checkIsTicketTypeScheduled({
              ticketType,
              allTicketTypes,
            });

            const isScheduled = ticketTypeScheduleResult.isScheduled;
            const hasInvalidReference = ticketTypeScheduleResult.hasInvalidReference;

            const isTicketTypeVisible = checkIsTicketTypeVisible({
              isSoldOut: ticketType.isSoldOut,
              allTicketTypes,
              ticketType,
              query,
              isOnSale: ticketType.isOnSale,
              isCanceled: isEventCanceled,
              isUnavailable: isTicketTypeUnavailable,
              hasInvalidReference,
              isScheduled,
            });

            return (
              <div
                key={ticketType._id}
                className={clsx(`cp_ticket-group-ticket-type block w-full`, {
                  // Only apply opacity to ticket type if ticket group is visible but ticket type is not
                  'opacity-25 pointer-events-none':
                    !isTicketTypeVisible && isPreview && isTicketGroupVisible,
                  hidden: !isTicketTypeVisible && !isPreview,
                })}
              >
                <TicketType
                  ticketType={ticketType}
                  selectedQuantity={getMaxSelectableQuantity(formTicketType?.quantity, ticketType)}
                  hasQuantitySelect
                  onQuantitySelect={setQuantity}
                  ticketGroup={ticketGroup}
                  isSoldOut={isSoldOut}
                  isCanceled={isEventCanceled}
                  isUnavailable={isUnavailable}
                  isOnSale={isOnSale}
                  isTicketTypeScheduled={ticketTypeScheduleResult.isScheduled}
                  ticketTypeHasInvalidReference={ticketTypeScheduleResult.hasInvalidReference}
                  isTicketGroupScheduled={isTicketGroupScheduled}
                  ticketGroupHasInvalidReference={ticketGroupHasInvalidReference}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  } else {
    return (
      <div
        className={clsx('cp_ticket-group', `cp_ticket-group-${ticketGroup.reference}`, {
          hidden: !isTicketGroupVisible && !isPreview,
        })}
      >
        {(showTicketGroupName || ticketGroup.description) && (
          <div className="mb-1">
            {showTicketGroupName && (
              <span className="cp_ticket-group-name font-semibold">{ticketGroup.name}</span>
            )}

            {ticketGroup.description && (
              <div className="cp_ticket-group-description text-gray-700 text-sm">
                {ticketGroup.description}
              </div>
            )}
          </div>
        )}

        <div
          className={clsx('cp_ticket-group-ticket-types', {
            'grid gap-2': ticketGroup.layout?.type === 'grid',
            '-space-y-px': ticketGroup.layout?.type !== 'grid',
          })}
          style={{
            ...(ticketGroup.layout?.type === 'grid' && {
              gridTemplateColumns: `repeat(${ticketGroup.layout?.columns}, minmax(0, 1fr))`,
            }),
          }}
        >
          {ticketTypes.map(ticketType => {
            const formTicketType = formTicketGroups?.[ticketGroup._id]?.find(
              formTicketGroup => formTicketGroup.ticketTypeId === ticketType._id,
            );

            // isSelected is true if the ticket type exists in the form and the quantity is greater than 0
            const isSelected = formTicketType?.quantity > 0;

            function onTicketTypeClick() {
              console.log('click', isSelected);
              // If the ticket type is already selected, set the quantity to 0 (deselect the ticket type)
              // If the ticket type is not selected, set the quantity to 1 (select the ticket type)
              if (isSelected) {
                unsetTicketType(ticketGroup._id, ticketType._id);
              } else {
                if (ticketGroup.ticketSelectionType === 'multiple') {
                  // if multiple select is allowed, set the quantity to 1
                  // leave the quantity of other ticket types in the same group as is
                  setTicketType(ticketGroup._id, ticketType._id);
                } else {
                  // if multiple select is not allowed, set the quantity to 1 and set the quantity of all other ticket types in the same group to 0
                  formTicketGroups?.[ticketGroup._id]?.forEach(formTicketGroup => {
                    if (formTicketGroup.ticketTypeId !== ticketType._id) {
                      unsetTicketType(ticketGroup._id, formTicketGroup.ticketTypeId);
                    }
                  });

                  setTicketType(ticketGroup._id, ticketType._id);
                }
              }

              if (onSelectTicketGroup && typeof onSelectTicketGroup === 'function') {
                onSelectTicketGroup(ticketGroup._id);
              }
            }

            const isSoldOut = isEventSoldOut || ticketGroup.isSoldOut || ticketType.isSoldOut;

            const isTicketTypeUnavailable = ticketType.availabilityStatus === 'unavailable';
            const isUnavailable = isTicketGroupUnavailable || isTicketTypeUnavailable;

            const isOnSale = ticketGroup.isOnSale && ticketType.isOnSale;

            const ticketTypeScheduleResult = checkIsTicketTypeScheduled({
              ticketType,
              allTicketTypes,
            });
            const isScheduled = ticketTypeScheduleResult.isScheduled;
            const hasInvalidReference = ticketTypeScheduleResult.hasInvalidReference;

            const isTicketTypeVisible = checkIsTicketTypeVisible({
              isSoldOut: ticketType.isSoldOut,
              allTicketTypes,
              ticketType,
              query,
              isOnSale: ticketType.isOnSale,
              isCanceled: isEventCanceled,
              isUnavailable: isTicketTypeUnavailable,
              hasInvalidReference,
              isScheduled,
            });

            return (
              <button
                key={ticketType._id}
                type="button"
                className={clsx(`cp_ticket-group-ticket-type block w-full`, {
                  'cursor-not-allowed': isSoldOut || !isOnSale || isEventCanceled || isUnavailable,
                  // Only apply opacity to ticket type if ticket group is visible but ticket type is not
                  'opacity-25 pointer-events-none':
                    !isTicketTypeVisible && isPreview && isTicketGroupVisible,
                  hidden: !isTicketTypeVisible && !isPreview,
                })}
                onClick={() => onTicketTypeClick(ticketType._id)}
                disabled={isSoldOut || !isOnSale || isEventCanceled || isUnavailable}
              >
                <TicketType
                  ticketType={ticketType}
                  selectedQuantity={getMaxSelectableQuantity(formTicketType?.quantity, ticketType)}
                  isSelected={isSelected}
                  ticketGroup={ticketGroup}
                  isSoldOut={isSoldOut}
                  isCanceled={isEventCanceled}
                  isUnavailable={isUnavailable}
                  isOnSale={isOnSale}
                  isTicketTypeScheduled={ticketTypeScheduleResult.isScheduled}
                  ticketTypeHasInvalidReference={ticketTypeScheduleResult.hasInvalidReference}
                  isTicketGroupScheduled={isTicketGroupScheduled}
                  ticketGroupHasInvalidReference={ticketGroupHasInvalidReference}
                />
              </button>
            );
          })}
        </div>
      </div>
    );
  }
}

function getMaxSelectableQuantity(quantity, ticketType) {
  const maxQuantity = ticketType.maxQuantity;
  return quantity > maxQuantity ? maxQuantity : quantity;
}
